<template>
  <div>
    <Header />
    <v-container fluid>
      <v-card :loading="loading" elevation="2" class="mb-6">
        <v-toolbar flat color="primary" dark>
          <v-toolbar-title>{{ app.name }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Suche"
            single-line
            hide-details
            filled
            dense
            rounded
            class="mx-2 mt-1"
            @keyup.enter="doSearch()"
          ></v-text-field>
          <v-btn icon @click="refresh">
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text>
          <v-row>
            <v-col cols="12">
              <div class="d-flex align-center mb-4">
                <h2 class="text-h5 font-weight-bold">Bestellungen</h2>
                <v-spacer></v-spacer>
                <v-btn-toggle v-model="filterMode" mandatory class="mr-3">
                  <v-btn small value="all" color="primary" class="px-2">
                    Alle
                  </v-btn>
                  <v-btn small value="errors" color="error" class="px-2">
                    Fehlerhafte
                  </v-btn>
                </v-btn-toggle>
                <v-chip small class="mr-2" color="amber darken-2" text-color="white">Fehlerhafte: {{ getOrderCountWithErrors() }}</v-chip>
                <v-chip small class="mr-2" color="green" text-color="white">Versandt: {{ getOrderCountByStatus('shipped') }}</v-chip>
                <v-chip small class="mr-2" color="orange" text-color="white">Ausstehend: {{ getOrderCountByStatus('pending') }}</v-chip>
                <v-chip small class="mr-2" color="red" text-color="white">Storniert: {{ getOrderCountByStatus('cancled') }}</v-chip>
              </div>
              
              <v-data-table 
                :v-if="orders" 
                :headers="orderHeaders" 
                :items="filteredOrders" 
                :sort-by="['updated', 'status']" 
                :sort-desc="true" 
                :search="search" 
                show-expand 
                items-per-page="100" 
                class="elevation-1"
                :footer-props="{
                  'items-per-page-options': [25, 50, 100, -1],
                  'items-per-page-text': 'Einträge pro Seite'
                }"
                :loading-text="'Daten werden geladen...'"
                :no-data-text="'Keine Daten verfügbar'"
                :no-results-text="'Keine Ergebnisse gefunden'"
              >
                <template v-slot:item.date="{ item }">
                  <span>{{ item.date | moment("DD.MM.YYYY HH:mm") }}</span>
                </template>
                
                <template v-slot:item.updated="{ item }">
                  <span>{{ item.updated | moment("DD.MM.YYYY HH:mm") }}</span>
                </template>

                <template v-slot:item.name1="{ item }">
                  <div class="font-weight-medium">{{ item.name1 }} {{ item.name2 }}</div>
                </template>

                <template v-slot:item.status="{ item }">
                  <v-chip 
                    small
                    :color="getStatusColor(item.status)" 
                    text-color="white"
                    label
                  >
                    {{ getStatusText(item.status) }}
                  </v-chip>
                </template>

                <template v-slot:item.hasError="{ item }">
                  <v-chip
                    x-small
                    :color="hasOrderItemErrors(item) ? 'error' : 'success'"
                    text-color="white"
                  >
                    {{ hasOrderItemErrors(item) ? 'Ja' : 'Nein' }}
                  </v-chip>
                </template>

                <template v-slot:item.actions="{ item }">
                  <v-btn 
                    small 
                    color="error" 
                    text 
                    @click="reportOrder(item)"
                    :disabled="isReporting[item.orderId]"
                    :loading="isReporting[item.orderId]"
                  >
                    <v-icon small left>mdi-flag</v-icon>
                    Melden
                  </v-btn>
                </template>

                <template v-slot:item.invoice="{ item }">
                  <div class="d-flex flex-column">
                    <v-select 
                      v-model="currentPrinter" 
                      :items="printers" 
                      item-text="name"
                      dense
                      solo
                      hide-details
                      class="mb-2"
                    ></v-select>
                    <v-btn 
                      v-if="item.invoice" 
                      small 
                      color="primary" 
                      @click="print(item.invoice)"
                      :loading="isPrinting[item.invoice]"
                    >
                      <v-icon small left>mdi-printer</v-icon>
                      Drucken
                    </v-btn>
                  </div>
                </template>

                <template v-slot:item.tracking="{ item }">
                  <a 
                    v-if="item.tracking && item.carrier == 'DHL'" 
                    target="_blank" 
                    class="text-decoration-none" 
                    :href="'https://www.dhl.de/de/privatkunden/pakete-empfangen/verfolgen.html?piececode=' + item.tracking"
                  >
                    <v-chip small outlined color="blue" class="pa-1">
                      <v-icon small left>mdi-truck-delivery</v-icon>
                      {{ item.tracking }}
                    </v-chip>
                  </a>
                  <span v-else-if="item.tracking && item.carrier != 'DHL'">
                    {{ item.tracking }}
                  </span>
                  <span v-else class="text-caption">Keine Tracking-Nr.</span>
                </template>

                <template v-slot:expanded-item="{ headers, item }">
                  <td :colspan="headers.length">
                    <div v-if="item.orderItems && item.orderItems.length > 0">
                      <v-alert 
                        v-if="hasOrderItemErrors(item)" 
                        dense 
                        type="warning" 
                        class="mb-3"
                      >
                        Diese Bestellung enthält keine Artikel mit Design-Daten (line1)
                      </v-alert>
                      
                      <v-card flat v-for="oi of item.orderItems" v-bind:key="oi.id" class="pa-3 ma-2" :class="{'error-border': !hasOrderItemLineData(oi)}">
                        <v-card-title class="d-flex align-center subtitle-1 font-weight-bold py-1">
                          <span>{{ oi.productName }}</span>
                          <v-chip v-if="!hasOrderItemLineData(oi)" x-small color="error" class="ml-2">Fehlende Daten</v-chip>
                        </v-card-title>
                        
                        <v-card-subtitle class="py-1">
                          <div class="d-flex flex-wrap">
                            <span class="mr-3">SKU: <strong>{{ oi.sku }}</strong></span>
                            <span class="mr-3">ID: <strong>{{ oi.id }}</strong></span>
                            <span>Menge: <strong>{{ oi.vQty }}</strong></span>
                            <span v-if="oi.motiv1" class="ml-3">Motiv: <strong>{{ oi.motiv1 }}</strong></span>
                          </div>
                        </v-card-subtitle>
                        
                        <v-card-text v-if="hasOrderItemLineData(oi)">
                          <v-row v-for="i in getVisibleLines(oi)" :key="i">
                            <v-col cols="2">Zeile {{ i }}:</v-col>
                            <v-col cols="10">
                              <img 
                                v-if="oi['line' + i] && oi['line' + i].startsWith('https')" 
                                :src="oi['line' + i]" 
                                alt="Image" 
                                class="max-height-150" 
                              />
                              <strong v-else>{{ oi["line" + i] }}</strong>
                            </v-col>
                          </v-row>
                        </v-card-text>
                        
                        <v-card-text v-else>
                          <v-alert dense type="error" outlined>
                            Fehler: Keine Design-Daten für dieses Produkt gefunden.
                            <div class="mt-2">
                              <v-btn small color="primary" @click="reportOrder(item)" class="mr-2">
                                <v-icon small left>mdi-flag</v-icon>
                                Bestellung melden
                              </v-btn>
                            </div>
                          </v-alert>
                        </v-card-text>
                      </v-card>
                    </div>
                    <v-alert v-else type="info" dense>Keine Bestellpositionen gefunden</v-alert>
                  </td>
                </template>
              </v-data-table>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-container>

    <!-- Bestätigungsdialog für Meldungen -->
    <v-dialog v-model="reportDialog" max-width="500px">
      <v-card>
        <v-card-title class="headline">Bestellung melden und stornieren</v-card-title>
        <v-card-text>
          <v-alert type="warning" dense class="mb-3">
            Diese Aktion wird die Bestellung automatisch stornieren. Dies kann nicht rückgängig gemacht werden.
          </v-alert>
          Möchten Sie diese Bestellung wirklich als problematisch melden?
          <v-textarea
            v-model="reportReason"
            label="Grund der Meldung und Stornierung"
            rows="3"
            outlined
            class="mt-4"
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="reportDialog = false">Abbrechen</v-btn>
          <v-btn color="error" text @click="confirmReport">Melden & Stornieren</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :timeout="3000" :color="snackbarColor">
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">
          Schließen
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import AppService from "@/services/AppService";
import PublicService from "@/services/PublicService";
import CustomerService from "@/services/CustomerService";
import TrackingService from "@/services/DHL/Tracking";
import OrderService from "@/services/OrderService"; // Neuer Service für die Melde-Funktion

import Header from "@/views/general/header";
import router from "@/plugins/router";

export default {
  name: "dashboard",
  components: {
    Header: Header,
  },
  data: () => ({
    loading: true,
    app: {},
    customers: [],
    search: null,
    orders: [],
    orderHeaders: [
      { text: "ID", value: "orderId" },
      { text: "PlentyId", value: "plentyId" },
      { text: "Ext. ID", value: "reference" },
      { text: "Datum", value: "date" },
      { text: "Name", value: "name1" },
      { text: "Status", value: "status" },
      { text: "Fehler", value: "hasError" },
      { text: "Zolldokument", value: "invoice" },
      { text: "Versandart", value: "carrier" },
      { text: "Tracking", value: "tracking" },
      { text: "Aktionen", value: "actions", sortable: false, align: "center" },
    ],
    printers: [{
      name: "Laser-Bereich",
      url: "http://localhost:631/printers/EPSN-A4-01"
    }, {
      name: "UV-Bereich",
      url: "http://localhost:631/printers/EPSN-A4-11"
    }],
    currentPrinter: null,
    snackbar: false,
    snackbarText: '',
    snackbarColor: 'success',
    
    // Neue Variablen für das Melden von Bestellungen
    reportDialog: false,
    selectedOrder: null,
    reportReason: '',
    isReporting: {},
    isPrinting: {},
    filterMode: 'all',
  }),
  computed: {
    filteredOrders() {
      if (this.filterMode === 'errors') {
        return this.orders.filter(order => this.hasOrderItemErrors(order));
      }
      return this.orders;
    }
  },
  mounted() {
    this.refresh();
    this.currentPrinter = this.printers[0];
  },
  methods: {
    // Neue Methoden für UI/UX
    getStatusColor(status) {
      switch(status) {
        case 'shipped': return 'green';
        case 'cancled': return 'red';
        case 'pending': return 'orange';
        case 'waiting': return 'blue';
        default: return 'grey';
      }
    },
    
    getStatusText(status) {
      switch(status) {
        case 'shipped': return 'Versandt';
        case 'cancled': return 'Storniert';
        case 'pending': return 'Ausstehend';
        case 'waiting': return 'Wartend';
        default: return status;
      }
    },
    
    getOrderCountByStatus(status) {
      return this.orders ? this.orders.filter(o => o.status === status).length : 0;
    },

    // Neue Methoden zur Fehlerprüfung
    hasOrderItemLineData(orderItem) {
      // Prüft, ob ein OrderItem mindestens eine line1-Eigenschaft hat
      return orderItem && (orderItem.line1 !== undefined);
    },
    
    hasOrderItemErrors(order) {
      // Bestellung gilt als fehlerhaft, wenn keine der Positionen ein line1-Attribut hat
      if (!order.orderItems || order.orderItems.length === 0) return false;
      
      // Prüfen, ob mindestens eine Position ein line1-Attribut hat
      const hasAtLeastOneItemWithLine1 = order.orderItems.some(oi => this.hasOrderItemLineData(oi));
      
      // Wenn keine Position line1 hat, ist die Bestellung fehlerhaft
      return !hasAtLeastOneItemWithLine1;
    },
    
    getOrderCountWithErrors() {
      return this.orders ? this.orders.filter(order => this.hasOrderItemErrors(order)).length : 0;
    },
    
    getVisibleLines(orderItem) {
      const visibleLines = [];
      for (let i = 1; i <= 10; i++) {
        if (orderItem['line' + i]) {
          visibleLines.push(i);
        }
      }
      return visibleLines;
    },
    
    // Methoden für die Melde-Funktion
    reportOrder(order) {
      this.selectedOrder = order;
      this.reportReason = '';
      this.reportDialog = true;
    },
    
    async confirmReport() {
      if (!this.selectedOrder) return;
      
      this.isReporting = { ...this.isReporting, [this.selectedOrder.orderId]: true };
      
      try {
        // Sichere Prüfung der customers-Array ohne optional chaining
        const customerId = this.customers && this.customers.length > 0 ? this.customers[0].id : null;
        
        await OrderService.reportOrder(this.selectedOrder.orderId, {
          reason: this.reportReason,
          orderId: this.selectedOrder.orderId,
          plentyId: this.selectedOrder.plentyId,
          externalId: this.selectedOrder.reference,
          customerId: customerId,
          appId: this.$route.params.id
        });
        
        // Status der Bestellung lokal aktualisieren
        if (this.selectedOrder) {
          this.selectedOrder.status = 'cancled';
          this.$forceUpdate();
        }
        
        this.snackbarText = 'Bestellung erfolgreich gemeldet und storniert';
        this.snackbarColor = 'success';
        this.snackbar = true;
      } catch (error) {
        console.error('Fehler beim Melden der Bestellung:', error);
        this.snackbarText = 'Fehler beim Melden und Stornieren der Bestellung';
        this.snackbarColor = 'error';
        this.snackbar = true;
      } finally {
        this.isReporting = { ...this.isReporting, [this.selectedOrder.orderId]: false };
        this.reportDialog = false;
      }
    },

    async doSearch() {
      let customerId = this.customers[0].id;
      let data = (await CustomerService.search(customerId, this.search)).data;
      for (let order of data) {
        if (!this.orders.find(o => o.orderId == order.orderId)) {
          this.orders.push(order);
        }
      }
    },
    
    async refreshTracking(plentyId) {
      try {
        await PublicService.getTrackingNumber(plentyId);
        this.loadOrders();
      } catch (err) {
        throw err;
      }
    },

    async trackingStatus(order) {
      let trackingNr = order.tracking;
      try {
        order.trackingStatus = (
          await TrackingService.track(trackingNr)
        ).data.shipments[0].status.statusCode;
      } catch (e) {
        order.trackingStatus = "unknown";
      }
      this.$forceUpdate();
    },

    async refresh() {
      this.loading = true;
      try {
        let data = (await AppService.getApp(this.$route.params.id)).data;
        if (data) {
          this.app = data;
          this.loadOrders();
        } else {
          router.replace("/apps");
        }
        this.loading = false;
      } catch (err) {
        if (err.response && err.response.status == 401) {
          router.replace("/auth/login");
        }
      }
    },
    
    async openLink(url) {
      let urls = url.split(",")
      for (let u of urls) {
        setTimeout(() => {
          window.open(u, "_blank");
        }, 500 * urls.indexOf(u));
      }
    },
    
    async print(url) {
      try {
        this.isPrinting = { ...this.isPrinting, [url]: true };
        
        console.log(this.currentPrinter);
        let printerURL = this.currentPrinter.url ? this.currentPrinter.url : this.printers.find(x => x.name == this.currentPrinter).url;
        await CustomerService.print(url, printerURL);
        
        this.snackbarText = 'Druckvorgang erfolgreich gestartet';
        this.snackbarColor = 'success';
        this.snackbar = true;
      } catch (error) {
        this.snackbarText = 'Fehler beim Drucken aufgetreten';
        this.snackbarColor = 'error';
        this.snackbar = true;
      } finally {
        this.isPrinting = { ...this.isPrinting, [url]: false };
      }
    },

    async loadOrders() {
      let appId = this.$route.params.id;

      this.customers = (await CustomerService.get(appId)).data;
      for (let c of this.customers) {
        this.orders = (await CustomerService.getOrders(c.id)).data;
      }
    },
  },
};
</script>

<style scoped>
.max-height-150 {
  max-height: 150px;
  object-fit: contain;
}

.error-border {
  border-left: 4px solid #f44336;
}
</style>